/*
  lab-pdp.js
  PDP conversion layer, dependency free, loaded with defer from
  sections/lab-main-product.liquid.

  1. Size guide modal: focus trap, Escape, scrim click. Markup lives in
     the section; this file only wires behaviour.
  2. Mobile sticky add to cart bar: slides in when the main buy button
     leaves the viewport upwards. The Add button submits the existing
     product form by clicking the real submit button, so both the demo
     cart interception and real Shopify submits keep working. CSS hides
     the bar at 768px and up and handles reduced motion.
*/
(function () {
  'use strict';

  /* Prices are subunit integers, matching lab-cart.js formatting. */
  function formatMoney(subunits) {
    var pounds = subunits / 100;
    var wholePence = subunits % 100 === 0;
    return '£' + pounds.toLocaleString('en-GB', {
      minimumFractionDigits: wholePence ? 0 : 2,
      maximumFractionDigits: wholePence ? 0 : 2
    });
  }

  function focusablesIn(container) {
    var nodes = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, function (node) {
      return node.offsetParent !== null || node === document.activeElement;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Size guide modal                                                    */
  /* ------------------------------------------------------------------ */

  function initSizeGuide(root) {
    var modal = root.querySelector('[data-lab-size-guide]');
    var opener = root.querySelector('[data-lab-size-guide-open]');
    if (!modal || !opener) return;

    var card = modal.querySelector('[data-lab-size-guide-card]');
    var scrim = modal.querySelector('[data-lab-size-guide-scrim]');
    var closeButton = modal.querySelector('[data-lab-size-guide-close]');
    if (!card) return;

    var isOpen = false;
    var lastFocused = null;

    function openModal() {
      if (isOpen) return;
      isOpen = true;
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.documentElement.classList.add('lab-pdp-lock');
      if (closeButton) {
        closeButton.focus();
      } else {
        card.focus();
      }
    }

    function closeModal() {
      if (!isOpen) return;
      isOpen = false;
      modal.hidden = true;
      document.documentElement.classList.remove('lab-pdp-lock');
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
      lastFocused = null;
    }

    function onKeydown(event) {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        closeModal();
        return;
      }
      if (event.key !== 'Tab') return;
      var items = focusablesIn(card);
      if (items.length === 0) {
        event.preventDefault();
        card.focus();
        return;
      }
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;
      if (event.shiftKey && (active === first || !card.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !card.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    opener.addEventListener('click', openModal);
    if (closeButton) closeButton.addEventListener('click', closeModal);
    if (scrim) scrim.addEventListener('click', closeModal);
    document.addEventListener('keydown', onKeydown);
  }

  /* ------------------------------------------------------------------ */
  /* Mobile sticky add to cart bar                                       */
  /* ------------------------------------------------------------------ */

  function initStickyBar(root) {
    var bar = root.querySelector('[data-lab-sticky-bar]');
    var buyButton = root.querySelector('[data-lab-submit]');
    if (!bar || !buyButton) return;

    var form = buyButton.closest('form');
    var addButton = bar.querySelector('[data-lab-sticky-add]');
    var priceEl = bar.querySelector('[data-lab-sticky-price]');
    if (!form || !addButton) return;

    function sync() {
      addButton.disabled = buyButton.disabled;
      if (buyButton.disabled) {
        addButton.textContent = buyButton.textContent.trim();
      } else {
        addButton.textContent = 'Add';
      }
      if (!priceEl) return;
      var raw = form.getAttribute('data-product-json');
      if (!raw) return;
      try {
        var payload = JSON.parse(raw);
        if (typeof payload.price === 'number') {
          priceEl.textContent = formatMoney(payload.price);
        }
      } catch (error) {
        /* Keep the server rendered price. */
      }
    }

    addButton.addEventListener('click', function () {
      buyButton.click();
    });

    /* The section's inline variant script updates the buy button and the
       form's data-product-json on option change; mirror both here. */
    if ('MutationObserver' in window) {
      var watcher = new MutationObserver(sync);
      watcher.observe(buyButton, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true
      });
      watcher.observe(form, { attributes: true, attributeFilter: ['data-product-json'] });
    }
    form.addEventListener('change', function () {
      window.setTimeout(sync, 0);
    });

    if (!('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i += 1) {
        var entry = entries[i];
        if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
          bar.classList.add('is-visible');
        } else {
          bar.classList.remove('is-visible');
        }
      }
    }, { threshold: 0 });
    observer.observe(buyButton);
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  function init() {
    var roots = document.querySelectorAll('[data-lab-pdp]');
    for (var i = 0; i < roots.length; i += 1) {
      initSizeGuide(roots[i]);
      initStickyBar(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
